import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAvailableIntegrations, useConnectedIntegrations, useCreateIntegration } from "@/hooks/useIntegrations";
import { usePermissions } from "@/contexts/usePermissions";
import { useReportPageReady } from "@/hooks/useReportPageReady";
import type { IntegrationsIntegrationDefinition } from "@/api-client/types.gen";
import { getApiErrorMessage } from "@/lib/errors";
import { showErrorToast } from "@/lib/toast";
import {
  isCapabilityBasedIntegrationDefinition,
  usesHostedGitHubAppInstall,
  usesHostedJiraOAuth,
  usesPrivateGitHubAppWizard,
} from "@/lib/integrations";
import { connectPrivateGitHubApp } from "@/lib/privateGitHubApp";
import { integrationDetailPath, integrationSetupPath, useIntegrationsBasePath } from "@/lib/integrationSettingsPaths";
import { getNextIntegrationName } from "@/pages/organization/settings/components/IntegrationSetup/lib";
import { buildIntegrationCatalog, filterIntegrationCatalog, integrationNameSet } from "@/lib/integrationCatalog";
import { startDirectJiraConnect } from "@/lib/startDirectJiraConnect";
import { areRequiredCreateFieldsFilled } from "@/ui/IntegrationCreateDialog/configurationFields";
import { useMe } from "@/hooks/useMe";

export function useIntegrationCatalog(organizationId: string) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const integrationsBasePath = useIntegrationsBasePath(organizationId);
  const { canAct, isLoading: permissionsLoading } = usePermissions();
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationsIntegrationDefinition | null>(null);
  const [integrationName, setIntegrationName] = useState("");
  const [configuration, setConfiguration] = useState<Record<string, unknown>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const canCreateIntegrations = canAct("integrations", "create");
  const canUpdateIntegrations = canAct("integrations", "update");

  const { data: availableIntegrations = [], isLoading: loadingAvailable } = useAvailableIntegrations();
  const { data: organizationIntegrations = [], isLoading: loadingInstalled } = useConnectedIntegrations(organizationId);
  const createIntegrationMutation = useCreateIntegration(organizationId, "integrations_page");
  const isLoading = loadingAvailable || loadingInstalled;
  const integrationNames = useMemo(() => integrationNameSet(organizationIntegrations), [organizationIntegrations]);
  const integrationCatalog = useMemo(
    () => buildIntegrationCatalog(availableIntegrations, organizationIntegrations),
    [availableIntegrations, organizationIntegrations],
  );
  const filteredIntegrationCatalog = useMemo(
    () => filterIntegrationCatalog(integrationCatalog, filterQuery),
    [filterQuery, integrationCatalog],
  );
  const canConnect =
    Boolean(integrationName.trim()) &&
    areRequiredCreateFieldsFilled(selectedIntegration?.configuration ?? [], configuration);

  useReportPageReady(!isLoading && !permissionsLoading);

  const actions = useIntegrationCatalogActions({
    organizationId,
    integrationsBasePath,
    navigate,
    canCreateIntegrations,
    canUpdateIntegrations,
    currentUserId: me?.id,
    organizationIntegrations,
    integrationNames,
    selectedIntegration,
    setSelectedIntegration,
    integrationName,
    setIntegrationName,
    configuration,
    setConfiguration,
    setIsModalOpen,
    createIntegrationMutation,
  });

  return {
    organizationId,
    isLoading,
    permissionsLoading,
    canCreateIntegrations,
    canUpdateIntegrations,
    filterQuery,
    setFilterQuery,
    integrationCatalog,
    filteredIntegrationCatalog,
    selectedIntegration,
    selectedInstructions: selectedIntegration?.instructions?.trim() ?? "",
    integrationName,
    setIntegrationName,
    canConnect,
    configuration,
    setConfiguration,
    isModalOpen,
    createIntegrationMutation,
    handlePrivateAppClick: (definition?: IntegrationsIntegrationDefinition) =>
      startCatalogPrivateGitHubApp({
        organizationId,
        integrationsBasePath,
        navigate,
        integrationNames,
        organizationIntegrations: organizationIntegrations ?? [],
        currentUserId: me?.id,
        createIntegrationMutation,
        definition,
      }),
    ...actions,
  };
}

function useIntegrationCatalogActions({
  organizationId,
  integrationsBasePath,
  navigate,
  canCreateIntegrations,
  canUpdateIntegrations,
  currentUserId,
  organizationIntegrations,
  integrationNames,
  selectedIntegration,
  setSelectedIntegration,
  integrationName,
  setIntegrationName,
  configuration,
  setConfiguration,
  setIsModalOpen,
  createIntegrationMutation,
}: {
  organizationId: string;
  integrationsBasePath: string;
  navigate: ReturnType<typeof useNavigate>;
  canCreateIntegrations: boolean;
  canUpdateIntegrations: boolean;
  currentUserId?: string;
  organizationIntegrations: ReturnType<typeof useConnectedIntegrations>["data"];
  integrationNames: Set<string>;
  selectedIntegration: IntegrationsIntegrationDefinition | null;
  setSelectedIntegration: (integration: IntegrationsIntegrationDefinition | null) => void;
  integrationName: string;
  setIntegrationName: (name: string) => void;
  configuration: Record<string, unknown>;
  setConfiguration: (configuration: Record<string, unknown>) => void;
  setIsModalOpen: (open: boolean) => void;
  createIntegrationMutation: ReturnType<typeof useCreateIntegration>;
}) {
  return {
    handleConnectClick: (integration: IntegrationsIntegrationDefinition) => {
      if (!canCreateIntegrations) {
        return;
      }
      if (usesHostedGitHubAppInstall(integration)) {
        startCatalogPrivateGitHubApp({
          organizationId,
          integrationsBasePath,
          navigate,
          integrationNames,
          organizationIntegrations: organizationIntegrations ?? [],
          currentUserId,
          createIntegrationMutation,
          definition: integration,
        });
        return;
      }
      if (
        startCatalogHostedJiraConnect({
          integration,
          organizationId,
          integrationsBasePath,
          integrationNames,
          organizationIntegrations,
          navigate,
          createIntegrationMutation,
        })
      ) {
        return;
      }
      if (isCapabilityBasedIntegrationDefinition(integration)) {
        if (!integration.name) {
          return;
        }
        navigate(integrationSetupPath(integrationsBasePath, integration.name));
        return;
      }
      setSelectedIntegration(integration);
      setIntegrationName(getNextIntegrationName(integration.name || "integration", integrationNames));
      setConfiguration({});
      setIsModalOpen(true);
    },
    handleConnect: () =>
      submitCatalogConnect({
        canCreateIntegrations,
        selectedIntegration,
        integrationName,
        configuration,
        createIntegrationMutation,
        integrationsBasePath,
        navigate,
        setIsModalOpen,
        setSelectedIntegration,
        setIntegrationName,
        setConfiguration,
      }),
    handleCloseModal: () => {
      setIsModalOpen(false);
      setSelectedIntegration(null);
      setIntegrationName("");
      setConfiguration({});
      createIntegrationMutation.reset();
    },
    openInstance: (providerName: string | undefined, integrationId: string | undefined, inSetup: boolean) => {
      if (!canUpdateIntegrations) {
        return;
      }
      if (providerName && inSetup) {
        navigate(integrationSetupPath(integrationsBasePath, providerName), { state: { integrationId } });
        return;
      }
      if (integrationId) {
        navigate(integrationDetailPath(integrationsBasePath, integrationId), { state: { tab: "configuration" } });
      }
    },
  };
}

async function submitCatalogConnect({
  canCreateIntegrations,
  selectedIntegration,
  integrationName,
  configuration,
  createIntegrationMutation,
  integrationsBasePath,
  navigate,
  setIsModalOpen,
  setSelectedIntegration,
  setIntegrationName,
  setConfiguration,
}: {
  canCreateIntegrations: boolean;
  selectedIntegration: IntegrationsIntegrationDefinition | null;
  integrationName: string;
  configuration: Record<string, unknown>;
  createIntegrationMutation: ReturnType<typeof useCreateIntegration>;
  integrationsBasePath: string;
  navigate: ReturnType<typeof useNavigate>;
  setIsModalOpen: (open: boolean) => void;
  setSelectedIntegration: (integration: IntegrationsIntegrationDefinition | null) => void;
  setIntegrationName: (name: string) => void;
  setConfiguration: (configuration: Record<string, unknown>) => void;
}) {
  if (!canCreateIntegrations || !selectedIntegration?.name) {
    return;
  }
  if (!integrationName.trim()) {
    showErrorToast("Integration name is required");
    return;
  }
  if (!areRequiredCreateFieldsFilled(selectedIntegration.configuration ?? [], configuration)) {
    showErrorToast("Enter every required field.");
    return;
  }

  try {
    const result = await createIntegrationMutation.mutateAsync({
      integrationName: selectedIntegration.name,
      name: integrationName,
      configuration,
    });
    setIsModalOpen(false);
    setSelectedIntegration(null);
    setIntegrationName("");
    setConfiguration({});
    const createdId = result.data?.integration?.metadata?.id;
    if (createdId) {
      navigate(integrationDetailPath(integrationsBasePath, createdId));
    }
  } catch (error) {
    showErrorToast(getApiErrorMessage(error, "Failed to create integration"));
  }
}

function startCatalogHostedJiraConnect({
  integration,
  organizationId,
  integrationsBasePath,
  integrationNames,
  organizationIntegrations,
  navigate,
  createIntegrationMutation,
}: {
  integration: IntegrationsIntegrationDefinition;
  organizationId: string;
  integrationsBasePath: string;
  integrationNames: Set<string>;
  organizationIntegrations: ReturnType<typeof useConnectedIntegrations>["data"];
  navigate: ReturnType<typeof useNavigate>;
  createIntegrationMutation: ReturnType<typeof useCreateIntegration>;
}): boolean {
  if (!usesHostedJiraOAuth(integration)) {
    return false;
  }

  void startDirectJiraConnect({
    organizationId,
    returnTo: integrationsBasePath,
    existingNames: integrationNames,
    connected: organizationIntegrations ?? [],
    onExistingReady: (integrationId) => {
      navigate(integrationDetailPath(integrationsBasePath, integrationId));
    },
    create: async (payload) => {
      const response = await createIntegrationMutation.mutateAsync(payload);
      return response.data;
    },
  }).catch((error) => {
    showErrorToast(getApiErrorMessage(error, "Failed to connect Jira"));
  });
  return true;
}

function startCatalogPrivateGitHubApp({
  organizationId,
  integrationsBasePath,
  navigate,
  integrationNames,
  organizationIntegrations,
  currentUserId,
  createIntegrationMutation,
  definition,
}: {
  organizationId: string;
  integrationsBasePath: string;
  navigate: ReturnType<typeof useNavigate>;
  integrationNames: Set<string>;
  organizationIntegrations: NonNullable<ReturnType<typeof useConnectedIntegrations>["data"]>;
  currentUserId?: string;
  createIntegrationMutation: ReturnType<typeof useCreateIntegration>;
  definition?: IntegrationsIntegrationDefinition;
}) {
  void connectPrivateGitHubApp({
    useWizard: usesPrivateGitHubAppWizard(definition),
    organizationId,
    returnTo: integrationsBasePath,
    integrationsBasePath,
    existingNames: integrationNames,
    connected: organizationIntegrations,
    currentUserId,
    goTo: navigate,
    create: async (payload) => {
      const response = await createIntegrationMutation.mutateAsync(payload);
      return response.data;
    },
  }).catch((error) => {
    showErrorToast(getApiErrorMessage(error, "Failed to connect GitHub"));
  });
}
